import React from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

/**
 * Reusable button component for consistent design and behavior.
 *
 * @component
 * @example
 * // Primary button
 * <Button text="Save" variant="primary" onClick={handleSave} />
 *
 * // Secondary button
 * <Button text="Cancel" variant="secondary" onClick={handleCancel} />
 *
 * @param {Object} props - Component props.
 * @param {string} [props.text="Button"] - The text to display inside the button.
 * @param {() => void} [props.onClick] - Callback function to handle click events.
 * @param {"primary" | "secondary"} [props.variant="primary"] - Button style variant.
 * @param {string} [props.className=""] - Additional Tailwind CSS classes for customization.
 * @param {boolean} [props.disabled=false] - Whether the button is disabled.
 * @param {boolean} [props.loading=false] - Whether the button is in loading state.
 * @returns {JSX.Element} A styled button element.
 */
const Button = ({
  text = "Button",
  onClick,
  variant = "primary",
  className = "",
  disabled = false,
  loading = false,
}) => {
  const baseStyles =
    "md:w-[262px] h-[59px] font-semibold py-2 transition uppercase focus:outline-none flex items-center justify-center gap-2";
    
  const variants = {
    primary:
      "bg-primary-default text-white border border-primary-default",
    secondary:
      "border border-primary-default text-orange-500",
  };

  const disabledStyles = "opacity-50 cursor-not-allowed";

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseStyles} ${variants[variant]} ${
        disabled || loading ? disabledStyles : ""
      } ${className}`}
    >
      {loading && <AiOutlineLoading3Quarters className="animate-spin" size={20} />}
      {text}
    </button>
  );
};

export default Button;